<template>
  <div
    class="main-menu"
    :class="{
      'main-menu--visible': state.visible
    }"
    :inert="!state.visible"
  >
    <div class="main-menu__menu-inner">
      <header class="main-menu__header">
        <AppButtonSearch url="/search/" title="zoeken" @click="onClick" />
        <button
          class="main-menu__button"
          aria-label="close"
          @click="onClick"
        >
          <AppIconLoader name="close" class="main-menu__icon" variant="custom" aria-hidden="true" />
        </button>
      </header>

      <ul class="main-menu__list">
        <li
          v-for="(menu_item, index) in mainMenu"
          :key="`menu-item_${index}`"
          ref="menuItems"
          :class="{
            'main-menu__menu-item--selected': state.selected === index,
            'main-menu__menu-item--prev': state.prev === index,
            'main-menu__menu-item--next': state.next === index,
            'main-menu__menu-item--no-allow-interaction': !state.allowInteraction,
            'main-menu__menu-item--small': state.hovered && state.prev !== index && state.next !== index && state.selected !== index,
          }"
          class="main-menu__menu-item"
          @mouseover="itemHover(index)"
          @mouseout="resetItemState"
          @mouseleave="resetItemState"
        >
          <NuxtLink
            :href="pageOrUrl(menu_item.item)"
            class="main-menu__link"
            @click="onClick"
            @focus="itemHover(index)"
          >
            {{ menu_item.item.label }}
          </NuxtLink>
        </li>
      </ul>

      <footer
        class="main-menu__footer"
      >
        <AppLogo class="main-menu__logo" @click="onClick" />
        <a
          href="https://www.npostart.nl/?ns_campaign=meernpo&ns_mchannel=omroep&ns_source=avrotros&ns_linkname=button"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Ga naar NPO Start"
          class="main-menu__footer-link"
        >
          <AppLogoNpoStart />
          <span class="main-menu__label">Ga naar NPO Start</span>
        </a>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import { useSettingsStore } from '~/store/settings'
import { TMenuItem } from '~/types/other-types'
import { joinSlugId } from '~/utils/slug-id'
import { usePopState } from '~/composables/usePopState'

const settingsStore = useSettingsStore()
const emit = defineEmits(['closeMenu'])

const { main_menu: mainMenu } = settingsStore.getNavigation
const menuItems = ref<HTMLElement[]>()

const props = defineProps({
  visible: { type: Boolean }
})

type TState = {
  visible: Boolean,
  selected?: Number,
  next?: Number,
  prev?: Number,
  allowInteraction: Boolean,
  hovered: Boolean
}

const pageOrUrl = (item: TMenuItem) => {
  const page = item.page[0]?.item
  const collection = item.page[0]?.collection
  if (page && collection) {
    const pageType = collection.split('_page')[0]
    const slugId = joinSlugId(page)
    if (pageType === 'content') {
      if (page.belongs_to?.slug) {
        return `/${pageType}-${page.belongs_to?.slug}/${slugId}/`
      } else {
        return `/${slugId}/`
      }
    }
    if (pageType === 'articles') {
      return `/archive/${slugId}/`
    } else {
      return `/${pageType}/${slugId}/`
    }
  } else {
    return item.url
  }
}

const state: TState = reactive({
  visible: props.visible,
  allowInteraction: false,
  hovered: false
})

watch(() => props.visible, (newValue) => {
  state.visible = newValue
  state.allowInteraction = false
  if (newValue) {
    window.setTimeout(() => {
      state.allowInteraction = true
    }, 325)
  }
})

const onClick = () => {
  emit('closeMenu')
}

const resetItemState = () => {
  state.next = undefined
  state.prev = undefined
  state.selected = undefined
  state.hovered = false
}

const itemHover = (index) => {
  resetItemState()

  state.hovered = true
  state.selected = index

  // if there are more than 1 items & you're not hovering over the last item in the list
  if (menuItems.value?.length > 1 && index !== menuItems.value?.length - 1) {
    state.next = index + 1
  }

  // get prev item, only if index > 0
  if (index > 0) {
    state.prev = index - 1
  }
}

usePopState(() => {
  onClick()
})

</script>

<style scoped lang="postcss">
.main-menu {
  background-color: var(--color-blue-prussian);
  color: var(--color-blue-aqua);
  pointer-events: none;
  position: fixed;
  right: 0px;
  top: 0px;
  z-index: 50;
  display: flex;
  height: 60px;
  width: 100vw;
  overflow: clip;
  opacity: 0;
  transform: translateY(-10vh);
  transition: height 250ms ease, opacity 125ms linear, transform 325ms ease-out;

  &__menu-inner {
    display: flex;
    width: 100%;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    padding-top: 1.5rem;
  }

  &__header {
    position: sticky;
    top: 0px;
    margin-bottom: 5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 1.5rem;
    padding-right: 1.5rem;

    @media (--xl) {
      margin-bottom: 2.5rem;
    }
  }

  &__button {
    position: absolute;
    right: 1.25rem;
    color: var(--color-blue-aqua);
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    @mixin focus-ring;

    &:hover {
      color: #fff;
    }

    @media (--md) {
      right: 40px;
    }

    @media (--xl) {
      margin-top: 2rem;
    }
  }

  &__icon {
    height: 1.25rem;
    width: auto;
  }

  &__list {
    display: flex;
    flex-direction: column;
    row-gap: 0.75rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    font-family: Midnight, sans-serif;
    font-size: 27px;
    text-transform: uppercase;
    line-height: 0.9;

    @media (--md) {
      row-gap: 0.5rem;
      font-size: 56px;
    }

    @media (--lg) {
      row-gap: 0.25rem;
    }

    @media (--xl) {
      font-size: 80px;
    }
  }

  &__menu-item {
    white-space: nowrap;
    outline: 2px solid transparent;
    outline-offset: 2px;
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

    &--no-allow-interaction {
      pointer-events: none;
    }

    @media (--xl) {
      &--selected {
        font-size: 180px;
      }

      &--next, &--prev {
        font-size: 80px;
      }

      &--small {
        font-size: 56px;
      }
    }
  }

  &__link {
    display: block;
    outline: 2px solid transparent;
    outline-offset: 2px;
  }

  &__footer {
    position: relative;
    margin-bottom: 1.5rem;
    margin-top: 2.5rem;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-end;

    @media (--md) {
      position: fixed;
      bottom: 0px;
      right: 0px;
      margin-bottom: 0px;
      margin-top: 0px;
    }
  }

  &__logo {
    width: 100%;
    max-width: 280px;
    color: var(--color-blue-aqua);

    @media (--md) {
      height: 260px;
      width: 350px;
      max-width: 350px;
      padding-left: 0px;
    }
  }

  &__footer-link {
    position: absolute;
    bottom: 0px;
    right: 0px;
    margin-bottom: 0px;
    margin-right: 1rem;
    height: 2rem;
    width: auto;
    @mixin focus-ring;

    @media (--md) {
      margin-bottom: 1.25rem;
    }
  }

  &__label {
    @mixin sr-only;
  }

  &--visible {
    pointer-events: auto;
    min-height: 100dvh;
    width: 100vw;
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
